// Contabilidad Module Types
// Double-entry bookkeeping types for accounting

// ============================================
// BASE TYPES
// ============================================

export type JournalType = "general" | "venta" | "compra" | "banco";
export type AccountType =
  | "activo"
  | "pasivo"
  | "patrimonio"
  | "ingreso"
  | "gasto";

// ============================================
// ENTITY TYPES
// ============================================

export interface Company {
  id: string;
  name: string;
  currency: string;
  country: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccountJournal {
  id: string;
  company_id: string;
  code: string;
  name: string;
  type: JournalType;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  company?: Company;
}

export interface Account {
  id: string;
  company_id: string;
  code: string;
  name: string;
  account_type: AccountType;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  company?: Company;
}

export interface AccountMovement {
  id: string;
  company_id: string;
  journal_id: string;
  move_number: string;
  move_date: string;
  ref: string;
  is_published: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  company?: Company;
  journal?: AccountJournal;
  lines?: AccountMoveLine[];
  // The AP pago that owns this asiento, when it was created by Pagos a Proveedor. An array because
  // supplier_payments.account_movement_id has no unique index (PostgREST embeds it as to-many).
  // `undefined` means "not selected", not "no pago" — see utils/supplierPaymentDraft.
  supplier_payment?: SupplierPaymentLink[];
}

/** Minimal projection of the `supplier_payments` row behind a pago-a-proveedor asiento. */
export interface SupplierPaymentLink {
  id: string;
  is_reversed: boolean;
}

export interface AccountMoveLine {
  id: string;
  move_id: string;
  account_id: string;
  description: string | null;
  debit: number;
  credit: number;
  created_at: string;
  // Joined fields
  account?: Account;
}

// ============================================
// FORM DATA TYPES (for create/update)
// ============================================

export type CompanyFormData = {
  name: string;
  currency: string;
  country: string;
};

export type JournalFormData = {
  company_id: string;
  code: string;
  name: string;
  type: JournalType;
};

export type AccountFormData = {
  company_id: string;
  code: string;
  name: string;
  account_type: AccountType;
  is_active?: boolean;
};

export type MovementFormData = {
  company_id: string;
  journal_id: string;
  move_date: string;
  ref: string;
  lines?: MoveLineInput[];
};

export type MoveLineFormData = {
  move_id: string;
  account_id: string;
  description?: string | null;
  debit: number;
  credit: number;
};

// Line input for creating movements with lines
export type MoveLineInput = {
  account_id: string;
  description?: string | null;
  debit: number;
  credit: number;
};

// ============================================
// LIST/FILTER TYPES
// ============================================

export interface CompanyFilters {
  search?: string;
}

export interface JournalFilters {
  search?: string;
  company_id?: string;
  type?: JournalType;
}

export interface AccountFilters {
  search?: string;
  company_id?: string;
  account_type?: AccountType;
  is_active?: boolean;
}

export interface MovementFilters {
  search?: string;
  company_id?: string;
  journal_id?: string;
  is_published?: boolean;
  date_from?: string;
  date_to?: string;
}

// ============================================
// DISPLAY HELPERS
// ============================================

export const JOURNAL_TYPE_LABELS: Record<JournalType, string> = {
  general: "General",
  venta: "Venta",
  compra: "Compra",
  banco: "Banco",
};

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  activo: "Activo",
  pasivo: "Pasivo",
  patrimonio: "Patrimonio",
  ingreso: "Ingreso",
  gasto: "Gasto",
};

export const ACCOUNT_TYPE_COLORS: Record<AccountType, string> = {
  activo: "bg-green-100 text-green-800",
  pasivo: "bg-red-100 text-red-800",
  patrimonio: "bg-blue-100 text-blue-800",
  ingreso: "bg-emerald-100 text-emerald-800",
  gasto: "bg-orange-100 text-orange-800",
};

export const CURRENCY_OPTIONS = [
  { value: "CLP", label: "CLP - Peso Chileno" },
  { value: "USD", label: "USD - Dólar Estadounidense" },
  { value: "EUR", label: "EUR - Euro" },
];

export const COUNTRY_OPTIONS = [
  { value: "CL", label: "Chile" },
  { value: "US", label: "Estados Unidos" },
  { value: "AR", label: "Argentina" },
  { value: "PE", label: "Perú" },
  { value: "CO", label: "Colombia" },
  { value: "MX", label: "México" },
];

// ============================================
// DIARIO SUMMARY TYPES
// ============================================

export interface PublishedMoveLine {
  line_id: string;
  move_id: string;
  account_id: string;
  account_code: string;
  account_name: string;
  description: string | null;
  debit: number;
  credit: number;
  move_number: string;
  move_date: string;
  move_ref: string;
  journal_id: string;
  journal_name: string;
  company_name: string;
}

// ============================================
// LIBRO MAYOR TYPES
// ============================================

export interface LibroMayorLine {
  account_id: string;
  account_code: string;
  account_name: string;
  account_type: AccountType;
  saldo_inicial: number;
  debitos_periodo: number;
  creditos_periodo: number;
  saldo_final: number;
}

export interface LibroMayorFilters {
  dateFrom: string;
  dateTo: string;
  companyId: string;
  accountId?: string;
  accountCodeFrom?: string;
  accountCodeTo?: string;
}

// ============================================
// VOUCHER GENERATOR TYPES (for auto-generation from facturas)
// ============================================

export interface FacturaForVoucher {
  id: string;
  bsale_number: string;
  document_type: string;
  emission_date: number; // Unix timestamp in seconds
  net_amount: number;
  tax_amount: number;
  total_amount: number;
  client_rut: string;
  client_name: string;
}

export interface VoucherLine {
  account_id: string;
  account_code: string;
  description: string;
  debit: number;
  credit: number;
}

export interface VoucherGeneratorConfig {
  companyId: string;
  journalId: string;
  receivablesAccountId: string;
  ivaAccountId: string;
  salesAccountId: string;
}

export interface VoucherValidationResult {
  isValid: boolean;
  totalDebit: number;
  totalCredit: number;
  difference: number;
  lineCount: number;
  facturaCount: number;
}

// ============================================
// ACCOUNT MAPPING TYPES (Financial Statements)
// ============================================

export type ReporteType = "Balance" | "Resultados";

export type ClaseType =
  | "Activo"
  | "Pasivo y Patrimonio"
  | "Costos"
  | "Gastos"
  | "Ingresos";

export interface AccountClassificationGroup {
  id: string;
  company_id: string;
  code: string;
  name: string;
  orden: number;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  company?: Company;
}

export interface AccountMappingTemplate {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Joined fields
  company?: Company;
  mappings_count?: number;
}

export interface AccountMapping {
  id: string;
  template_id: string;
  account_id: string;
  reporte: ReporteType;
  clase: ClaseType;
  orden_clase: number;
  orden_tipo: number;
  tipo: string | null;
  sub_clase: string;
  orden_subclase: number;
  sub_clase_2: string;
  orden_subclase_2: number;
  grupo_cuenta_id: string | null;
  orden_grupo_cuenta: number;
  clave: number | null;
  signo: number | null;
  clase_1: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  account?: Account;
  grupo_cuenta?: AccountClassificationGroup;
  template?: AccountMappingTemplate;
}

// Form data types for account mapping
export type ClassificationGroupFormData = {
  company_id: string;
  code: string;
  name: string;
  orden?: number;
};

export type MappingTemplateFormData = {
  company_id: string;
  name: string;
  description?: string;
  is_active?: boolean;
};

export type AccountMappingFormData = {
  template_id: string;
  account_id: string;
  reporte: ReporteType;
  clase: ClaseType;
  orden_clase?: number;
  orden_tipo?: number;
  tipo?: string;
  sub_clase: string;
  orden_subclase?: number;
  sub_clase_2: string;
  orden_subclase_2?: number;
  grupo_cuenta_id?: string;
  orden_grupo_cuenta?: number;
  clave?: number;
  signo?: number;
  clase_1?: string;
};

// Filter types
export interface ClassificationGroupFilters {
  search?: string;
  company_id?: string;
}

// =============================================================================
// Accounting categories (product-category accounting config — Odoo-style)
// =============================================================================

/** AVCO = costo promedio. Standard/FIFO reserved for future use. */
export type CostingMethod = "average" | "standard" | "fifo";
/** perpetual = permanente (al facturar); manual = periódico. */
export type InventoryValuation = "manual" | "perpetual";

export interface AccountingCategory {
  id: string;
  company_id: string;
  name: string;
  parent_id: string | null;
  costing_method: CostingMethod;
  inventory_valuation: InventoryValuation;
  income_account_id: string | null;
  expense_account_id: string | null;
  inventory_account_id: string | null;
  inventory_variation_account_id: string | null;
  created_at: string;
  updated_at: string;
  // Joined account rows (code + name) for display.
  income_account?: Pick<Account, "id" | "code" | "name"> | null;
  expense_account?: Pick<Account, "id" | "code" | "name"> | null;
  inventory_account?: Pick<Account, "id" | "code" | "name"> | null;
  inventory_variation_account?: Pick<Account, "id" | "code" | "name"> | null;
}

export interface AccountingCategoryFormData {
  company_id: string;
  name: string;
  parent_id?: string | null;
  costing_method: CostingMethod;
  inventory_valuation: InventoryValuation;
  income_account_id?: string | null;
  expense_account_id?: string | null;
  inventory_account_id?: string | null;
  inventory_variation_account_id?: string | null;
}

export interface AccountingCategoryFilters {
  search?: string;
  company_id?: string;
}

export interface MappingTemplateFilters {
  search?: string;
  company_id?: string;
  is_active?: boolean;
}

export interface AccountMappingFilters {
  search?: string;
  template_id?: string;
  reporte?: ReporteType;
  clase?: ClaseType;
  grupo_cuenta_id?: string;
}

// Constants for account mapping
export const REPORTE_OPTIONS: ReporteType[] = ["Balance", "Resultados"];

export const CLASE_OPTIONS: ClaseType[] = [
  "Activo",
  "Pasivo y Patrimonio",
  "Costos",
  "Gastos",
  "Ingresos",
];

export const TIPO_OPTIONS = [
  "Activo",
  "Pasivo",
  "Patrimonio",
  "Ingreso",
  "Costo",
  "Gasto",
] as const;

export const SUB_CLASE_OPTIONS = [
  "Activo",
  "Pasivo",
  "Patrimonio",
  "Ventas",
  "Costo de Ventas",
  "Costos de Distribucion",
  "Administracion y Ventas",
  "Otros Ingresos",
] as const;

export const SUB_CLASE_2_OPTIONS = [
  "Activo Corriente",
  "Activo No corriente",
  "Pasivo Corriente",
  "Pasivo no Corriente",
  "Patrimonio",
  "Costo de Ventas",
  "Costos de Explotacion",
  "Gastos del Personal",
  "Otros gastos administracion",
  "Servicios prestados por terceros",
  "Gastos fuera de la explotacion",
  "Otros Activos",
  "Otros Ingresos",
  "Ventas",
] as const;

export const CLASE_TYPE_COLORS: Record<ClaseType, string> = {
  Activo: "bg-green-100 text-green-800",
  "Pasivo y Patrimonio": "bg-blue-100 text-blue-800",
  Costos: "bg-orange-100 text-orange-800",
  Gastos: "bg-red-100 text-red-800",
  Ingresos: "bg-emerald-100 text-emerald-800",
};

export const REPORTE_TYPE_COLORS: Record<ReporteType, string> = {
  Balance: "bg-indigo-100 text-indigo-800",
  Resultados: "bg-purple-100 text-purple-800",
};

// ============================================
// BALANCE SHEET (ESTADO DE SITUACION FINANCIERA) TYPES
// ============================================

export interface BalanceSheetLine {
  account_id: string;
  account_code: string;
  account_name: string;
  reporte: string;
  clase: string;
  orden_clase: number;
  tipo: string | null;
  orden_tipo: number;
  sub_clase: string;
  orden_subclase: number;
  sub_clase_2: string;
  orden_subclase_2: number;
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_grupo_cuenta: number;
  signo: number | null;
  saldo_inicial: number;
  debitos_periodo: number;
  creditos_periodo: number;
  saldo_final: number;
}

export interface BalanceSheetFilters {
  dateFrom: string;
  dateTo: string;
  companyId: string;
  templateId: string;
}

// Grouped data structures for hierarchical display
export interface BalanceSheetAccount {
  account_id: string;
  account_code: string;
  account_name: string;
  saldo_final: number;
}

export interface BalanceSheetGrupo {
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_grupo_cuenta: number;
  total: number;
  accounts: BalanceSheetAccount[];
}

export interface BalanceSheetSubClase2 {
  sub_clase_2: string;
  orden_subclase_2: number;
  total: number;
  grupos: BalanceSheetGrupo[];
}

export interface BalanceSheetSubClase {
  sub_clase: string;
  orden_subclase: number;
  total: number;
  subClases2: BalanceSheetSubClase2[];
}

export interface BalanceSheetClase {
  clase: string;
  orden_clase: number;
  total: number;
  subClases: BalanceSheetSubClase[];
}

export interface BalanceSheetGroupedData {
  clases: BalanceSheetClase[];
  totalActivo: number;
  totalPasivoYPatrimonio: number;
  diferencia: number;
}

// ============================================================
// CSV Import Types
// ============================================================

export interface AccountMappingCSVRow {
  codigo: string;
  cuenta: string;
  tipo: string;
  reporte: string;
  clase: string;
  orden_clase: number;
  orden_tipo: number;
  sub_clase: string;
  orden_subclase: number;
  sub_clase_2: string;
  orden_subclase_2: number;
  grupo_cuenta: string;
  orden_grupo_cuenta: number;
  clave: string;
  signo: number;
  clase_1: string;
}

export interface AccountMappingImportResult {
  success: boolean;
  imported: number;
  errors: Array<{ row: number; error: string }>;
  skipped: number;
}

// ============================================================
// Income Statement Types
// ============================================================

// Raw data from database with 3 amount columns
export interface IncomeStatementLine {
  account_id: string;
  account_code: string;
  account_name: string;
  clase: string; // Ingresos, Costos, Gastos
  sub_clase: string;
  sub_clase_2: string;
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_clase: number;
  orden_subclase: number;
  orden_subclase_2: number;
  orden_grupo_cuenta: number;
  signo: number; // Usually -1

  // THREE amount columns
  saldo_acumulado_anterior: number; // Cumulative up to previous month
  saldo_mes_actual: number; // Current month only
  saldo_acumulado_total: number; // Total cumulative
}

// Account level with 3 amounts
export interface IncomeStatementAccount {
  account_id: string;
  account_code: string;
  account_name: string;
  anterior: number;
  actual: number;
  total: number;
}

// Grupo level with 3 amounts
export interface IncomeStatementGrupo {
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_grupo_cuenta: number;
  anterior: number;
  actual: number;
  total: number;
  accounts: IncomeStatementAccount[];
}

// SubClase2 level with 3 amounts
export interface IncomeStatementSubClase2 {
  sub_clase_2: string;
  orden_subclase_2: number;
  anterior: number;
  actual: number;
  total: number;
  grupos: IncomeStatementGrupo[];
}

// SubClase level with 3 amounts
export interface IncomeStatementSubClase {
  sub_clase: string;
  orden_subclase: number;
  anterior: number;
  actual: number;
  total: number;
  subClases2: IncomeStatementSubClase2[];
}

// Clase level with 3 amounts
export interface IncomeStatementClase {
  clase: string;
  orden_clase: number;
  anterior: number;
  actual: number;
  total: number;
  subClases: IncomeStatementSubClase[];
}

// Final grouped data with 3-column totals and summary
export interface IncomeStatementGroupedData {
  clases: IncomeStatementClase[];

  // Totals for each column
  totals: {
    ingresos: { anterior: number; actual: number; total: number };
    costos: { anterior: number; actual: number; total: number };
    gastos: { anterior: number; actual: number; total: number };
    utilidadBruta: { anterior: number; actual: number; total: number };
    utilidadOperacional: { anterior: number; actual: number; total: number };
    margenOperacional: number; // % based on total column
  };
}

// Filters - Month and Year based
export interface IncomeStatementFilters {
  year: number;
  month: number; // 1-12
  companyId: string;
  templateId: string;
}

// ============================================================
// Budget (Control Presupuestario) Types
// ============================================================

export type BudgetStatus = "draft" | "approved";

export interface Budget {
  id: string;
  company_id: string;
  fiscal_year: number;
  name: string;
  status: BudgetStatus;
  notes: string | null;
  created_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  // Joined fields
  company?: Company;
  lines_count?: number;
}

// Amounts are PRESENTATION-POSITIVE (Ventas 120.000.000, Remuneraciones
// 25.000.000). Sign conversion to the report convention happens only inside
// the get_budget_vs_actual RPC. See docs/business-rules/budget-control.md.
export interface BudgetLine {
  id: string;
  budget_id: string;
  account_id: string;
  month: number; // 1-12
  amount: number;
  created_at: string;
  updated_at: string;
  // Joined fields
  account?: Account;
}

export type BudgetFormData = {
  company_id: string;
  fiscal_year: number;
  name: string;
  notes?: string;
};

// Cell input for batch upserts (onConflict: budget_id,account_id,month)
export type BudgetLineInput = {
  account_id: string;
  month: number;
  amount: number;
};

export interface BudgetFilters {
  company_id?: string;
  fiscal_year?: number;
  status?: BudgetStatus;
}

export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  draft: "Borrador",
  approved: "Aprobado",
};

// Income-statement clase names shared by the real and budget reports
export const RESULTADOS_CLASES = {
  ingresos: "Ingresos",
  costos: "Costos",
  gastos: "Gastos",
} as const;

// ============================================================
// Budget vs Actual (Presupuesto vs Real) Types
// ============================================================

// Raw row from the get_budget_vs_actual RPC. Both series arrive in the
// signed report convention (ingresos +, costos/gastos -), so
// desviacion = real - budget is ALWAYS favorable when positive.
export interface BudgetVsActualLine {
  account_id: string;
  account_code: string;
  account_name: string;
  clase: string | null;
  orden_clase: number | null;
  sub_clase: string | null;
  orden_subclase: number | null;
  sub_clase_2: string | null;
  orden_subclase_2: number | null;
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_grupo_cuenta: number | null;
  signo: number | null;
  real_mes: number;
  real_acumulado: number;
  budget_mes: number;
  budget_acumulado: number;
  has_mapping: boolean;
}

// Four amounts carried on every hierarchy node (mes + acumulado), so the
// Mes/Acumulado toggle switches views without regrouping
export interface BudgetVsActualValues {
  budgetMes: number;
  realMes: number;
  budgetAcumulado: number;
  realAcumulado: number;
}

export interface BudgetVsActualAccount extends BudgetVsActualValues {
  account_id: string;
  account_code: string;
  account_name: string;
}

export interface BudgetVsActualGrupo extends BudgetVsActualValues {
  grupo_cuenta_id: string | null;
  grupo_cuenta_code: string | null;
  grupo_cuenta_name: string | null;
  orden_grupo_cuenta: number;
  accounts: BudgetVsActualAccount[];
}

export interface BudgetVsActualSubClase2 extends BudgetVsActualValues {
  sub_clase_2: string;
  orden_subclase_2: number;
  grupos: BudgetVsActualGrupo[];
}

export interface BudgetVsActualSubClase extends BudgetVsActualValues {
  sub_clase: string;
  orden_subclase: number;
  subClases2: BudgetVsActualSubClase2[];
}

export interface BudgetVsActualClase extends BudgetVsActualValues {
  clase: string;
  orden_clase: number;
  subClases: BudgetVsActualSubClase[];
}

export interface BudgetVsActualGroupedData {
  clases: BudgetVsActualClase[];
  // Budgeted accounts missing from the template (has_mapping = false) —
  // surfaced in a mismatch alert, never silently dropped
  unmapped: BudgetVsActualLine[];
  totals: {
    ingresos: BudgetVsActualValues;
    costos: BudgetVsActualValues;
    gastos: BudgetVsActualValues;
    utilidadBruta: BudgetVsActualValues;
    utilidadOperacional: BudgetVsActualValues;
  };
}

export interface BudgetVsActualFilters {
  year: number;
  month: number; // 1-12
  companyId: string;
  templateId: string;
  budgetId: string; // which scenario to compare against ("" => budget shown as 0)
}

// ============================================================
// Budget Excel Import Types
// ============================================================

export interface BudgetImportRow {
  rowNumber: number;
  code: string;
  accountName: string;
  accountId: string | null;
  amounts: number[]; // 12 values, Ene..Dic (empty cells => 0)
  total: number;
  error?: string;
  warning?: string;
}

export interface BudgetParseResult {
  rows: BudgetImportRow[];
  validRows: BudgetImportRow[];
  errorCount: number;
  warningCount: number;
}
